import { CircularProgress, Container } from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';
import { useNavigate } from 'react-router-dom';
import InfiniteScroll from 'react-infinite-scroll-component';

import Alerts, { AlertObj } from '../../components/Alerts/Alerts';
import PageLayout from '../../components/Layout/PageLayout';
import Poll from '../../components/Poll/Poll';
import {
    AnonymousUserContext,
    AnonymousUserContextType,
} from '../../contexts/anonUser';
import { SessionContext, SessionContextType } from '../../contexts/session';
import {
    CookieNames,
    CookieNotificationWeight,
    CookiesType,
} from '../../utils/cookies/types';
import {
    HomeController,
    HomeControllerState,
    Notifications,
} from './HomeController';
import { RoutePaths } from '../../components/core/AppRouter/AppRouter';
import { PollRecord } from '../../models/Poll';
import CardContainer from '../../components/SideColumnsContent/CardContainer/CardContainer';
import { UserActivity, UserActivityContext } from '../../contexts/userActivity';
import UserStatsModal from './UserStatsModal/UserStatsModal';

import './Home.scss';

const Home = (): JSX.Element => {
    const { session, setSession } =
        useContext<SessionContextType>(SessionContext);
    const { anonUser, setAnonUser } =
        useContext<AnonymousUserContextType>(AnonymousUserContext);
    const [polls, setPolls] = useState<PollRecord[]>([]);
    const [alerts, setAlerts] = useState<AlertObj[]>([]);
    const [cookies, setCookie] = useCookies<CookieNames, CookiesType>([
        CookieNames.Notifications,
    ]);
    const navigate = useNavigate();
    const [homeControllerState, setHomeControllerState] =
        useState<HomeControllerState>({
            endOfList: false,
        });
    const [userActivity, setUserActivity] = useState<UserActivity>({
        votes: 0,
        pollsCreated: 0,
        comments: 0,
        shares: 0,
        activitySince: Date.now(),
    });
    const [openModal, setOpenModal] = useState(false);

    // Evaluate user activity to show regular progress
    useEffect(() => {
        if (
            (session.user !== undefined || anonUser.user !== undefined) &&
            HomeController.alertDueToUserActivity(userActivity)
        ) {
            setOpenModal(true);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userActivity]);

    // Retrieve initial polls
    useEffect(() => {
        if (!Boolean(polls.length)) {
            nextHandler();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [anonUser, session]);

    // If no polls, redirect to another page
    useEffect(() => {
        if (homeControllerState.endOfList && polls.length <= 0) {
            navigate(`/${RoutePaths.NewPoll}`);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [homeControllerState.endOfList]);

    // Manage home notifications
    useEffect(() => {
        if (
            !cookies[CookieNames.Notifications] ||
            !(
                Number(cookies[CookieNames.Notifications]) &
                CookieNotificationWeight.Intro
            )
        ) {
            setAlerts([
                {
                    text: Notifications.Intro,
                    severity: 'info',
                    onClose: () => {
                        setCookie(
                            CookieNames.Notifications,
                            Number(cookies[CookieNames.Notifications]) |
                                CookieNotificationWeight.Intro,
                            {
                                path: '/',
                                // Expires in 100 years (implementation of "never expires")
                                expires: new Date(
                                    Date.now() + 100 * 365 * 24 * 60 * 60000,
                                ),
                                domain: window.location.hostname,
                                secure: true,
                            },
                        );
                    },
                },
            ]);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const nextHandler = async () => {
        await HomeController.readMorePolls(
            { session, setSession },
            { anonUser, setAnonUser },
            polls,
            setPolls,
            homeControllerState,
            setHomeControllerState,
        );
    };

    return (
        <UserActivityContext.Provider value={{ userActivity, setUserActivity }}>
            <PageLayout>
                <Container id='home-page-container'>
                    <Alerts alerts={alerts} setAlerts={setAlerts} />
                    <InfiniteScroll
                        dataLength={polls.length}
                        next={nextHandler}
                        hasMore={!homeControllerState.endOfList}
                        loader={
                            <CardContainer className='poll-card-container'>
                                <CircularProgress color='primary' />
                            </CardContainer>
                        }
                        scrollThreshold='1000px'
                    >
                        {polls.map((poll) => (
                            <Poll key={poll.id} poll={poll} />
                        ))}
                    </InfiniteScroll>
                    <UserStatsModal open={openModal} setOpen={setOpenModal} />
                </Container>
            </PageLayout>
        </UserActivityContext.Provider>
    );
};

export default Home;
